import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { X, Check, AlertTriangle, DollarSign, Clock, Shield, Star, MessageCircle } from 'lucide-react';

const ComparisonMap = () => {
  const handleWhatsAppClick = () => {
    window.open('https://wa.me/5512997176654?text=Olá! Gostaria de uma consultoria para economizar na montagem do meu PC!', '_blank');
  };

  const problemsWithoutService = [
    {
      icon: DollarSign,
      text: "Paga mais caro por não conhecer o mercado",
      color: "text-red-500"
    },
    {
      icon: AlertTriangle,
      text: "Escolhe peças com menos garantia",
      color: "text-red-500"
    },
    {
      icon: X,
      text: "Peças sem muita qualidade",
      color: "text-red-500"
    },
    {
      icon: Clock,
      text: "Gasta muito tempo pesquisando",
      color: "text-red-500"
    },
    {
      icon: AlertTriangle,
      text: "Risco de incompatibilidade",
      color: "text-red-500"
    },
    {
      icon: X,
      text: "Sem suporte especializado",
      color: "text-red-500"
    }
  ];

  const benefitsWithService = [
    {
      icon: Check,
      text: "Economia garantida com cupons",
      color: "text-green-500"
    },
    {
      icon: Shield,
      text: "Peças com máxima garantia",
      color: "text-green-500"
    },
    {
      icon: Star,
      text: "Componentes de alta qualidade",
      color: "text-green-500"
    },
    {
      icon: Clock,
      text: "Processo rápido e eficiente",
      color: "text-green-500"
    },
    {
      icon: Check,
      text: "100% compatibilidade",
      color: "text-green-500"
    },
    {
      icon: Star,
      text: "Suporte especializado",
      color: "text-green-500"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-red-500/10 rounded-full animate-pulse" />
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-green-500/10 rounded-full animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/4 w-20 h-20 bg-orange-500/10 rounded-full animate-bounce delay-500" />
      </div>

      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <Badge className="mb-4 bg-orange-100 text-orange-800 hover:bg-orange-100">
            Comparação Gamer
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Por que Escolher a
            <span className="block text-orange-500">Tecnote?</span>
          </h2>
          <p className="text-xl text-gray-300">
            Veja a diferença entre fazer sozinho e ter um especialista ao seu lado
          </p>
        </div>

        {/* Comparison Map */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Without Service - Left Side */}
          <div className="relative">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <X className="w-10 h-10 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-red-400 mb-2">Sem a Tecnote</h3>
              <p className="text-gray-400">O caminho difícil e caro</p>
            </div>

            <div className="space-y-4">
              {problemsWithoutService.map((problem, index) => {
                const Icon = problem.icon;
                return (
                  <div 
                    key={index}
                    className="flex items-center space-x-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4 transform transition-all duration-500 hover:scale-105"
                    style={{ 
                      animationDelay: `${index * 200}ms`,
                      animation: 'slideInLeft 0.6s ease-out forwards'
                    }}
                  >
                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className={`w-6 h-6 ${problem.color}`} />
                    </div>
                    <span className="text-gray-300 font-medium">{problem.text}</span>
                  </div>
                );
              })}
            </div>

            {/* Gaming PC Image Placeholder - Sad */}
            <div className="mt-8 text-center">
              <div className="w-32 h-32 bg-red-500/10 rounded-2xl mx-auto flex items-center justify-center">
                <div className="text-6xl">😞</div>
              </div>
              <p className="text-red-400 mt-2 font-semibold">Setup Problemático</p>
            </div>
          </div>

          {/* VS Divider */}
          <div className="hidden lg:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center animate-spin-slow">
              <span className="text-white font-bold text-lg">VS</span>
            </div>
          </div>

          {/* With Service - Right Side */}
          <div className="relative">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse delay-500">
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-green-400 mb-2">Com a Tecnote</h3>
              <p className="text-gray-400">O caminho inteligente e econômico</p>
            </div>

            <div className="space-y-4">
              {benefitsWithService.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div 
                    key={index}
                    className="flex items-center space-x-4 bg-green-500/10 border border-green-500/20 rounded-lg p-4 transform transition-all duration-500 hover:scale-105"
                    style={{ 
                      animationDelay: `${(index + 3) * 200}ms`,
                      animation: 'slideInRight 0.6s ease-out forwards'
                    }}
                  >
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className={`w-6 h-6 ${benefit.color}`} />
                    </div>
                    <span className="text-gray-300 font-medium">{benefit.text}</span>
                  </div>
                );
              })}
            </div>

            {/* Gaming PC Image Placeholder - Happy */}
            <div className="mt-8 text-center">
              <div className="w-32 h-32 bg-green-500/10 rounded-2xl mx-auto flex items-center justify-center">
                <div className="text-6xl">🎮</div>
              </div>
              <p className="text-green-400 mt-2 font-semibold">Setup Perfeito</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-orange-500/10 to-green-500/10 border border-orange-500/20 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Faça a Escolha Inteligente!
            </h3>
            <p className="text-gray-300 mb-6">
              Não arrisque seu investimento. Tenha um especialista ao seu lado 
              e economize tempo e dinheiro no seu setup gamer.
            </p>
            
            <Button 
              size="lg" 
              onClick={handleWhatsAppClick}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <MessageCircle className="w-5 h-5 mr-3" />
              Quero Economizar com a Tecnote!
            </Button>
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 4s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default ComparisonMap;